import React, { useState, useEffect, useRef } from 'react';
import { getSpriteUrl } from '../logic/assetUtils';
import Settings from '../components/Settings';
import AnimatedTitle from '../components/AnimatedTitle';
import { FallingLeaves } from '../common/Effects';
import { getGsapModifiers, AnimatedBackground } from '../common/AnimatedBackgrounds';
import { gsap } from 'gsap';

export default function Home({ gameLogic, onStartGame }) {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const animatedBackgroundRef = useRef(null);
  const timelineRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowButtons(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (animatedBackgroundRef.current) {
      const imageRef = animatedBackgroundRef.current.imageRef;
      const modifiers = getGsapModifiers(animatedBackgroundRef);

      timelineRef.current = gsap.timeline();

      timelineRef.current.to(
        imageRef,
        {
          duration: 5,
          scale: 1.1,
          ease: 'power2.inOut',
          modifiers,
        },
        'start'
      );

      timelineRef.current.to(
        imageRef,
        {
          duration: 30,
          x: '-200px',
          ease: 'power1.inOut',
          repeat: -1,
          yoyo: true,
          modifiers,
        },
        'start+=2'
      );
    }

    return () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
    };
  }, []);

  const handleStartGame = () => {
    setButtonsDisabled(true);
    setIsTransitioning(true);

    if (timelineRef.current) {
      timelineRef.current.kill();
    }

    setTimeout(() => {
      onStartGame();
    }, 1000);
  };

  return (
    <div className="w-screen h-screen relative overflow-hidden bg-[#0c1220]">
      {/* Background Layer - z-0 */}
      <AnimatedBackground
        ref={animatedBackgroundRef}
        imageSrc={getSpriteUrl('homePageBackground')}
      />

      {/* Space Particles Layer - z-5 */}
      <FallingLeaves color="#4a9eff" count={30} />

      {/* Decorative Elements - z-10 */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        {/* Scanning lines effect */}
        <div className="absolute inset-0 bg-[linear-gradient(transparent_50%,rgba(74,158,255,0.05)_50%)] bg-[length:100%_4px] animate-scan" />
        
        {/* Nebula effects */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#4a9eff] rounded-full opacity-[0.03] blur-[100px] animate-float" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#7b5ee7] rounded-full opacity-[0.03] blur-[100px] animate-float-delayed" />
      </div>

      {!showSettings && (
        <>
          {/* Content Layer - z-20 */}
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-start pt-20 pointer-events-none">
            <div className="w-[400px] h-[200px]">
              <AnimatedTitle />
            </div>
          </div>

          {/* Interactive Layer - z-30 */}
          <div className="absolute inset-0 z-30 flex items-center justify-center">
            <div className="flex flex-col gap-6 mt-32">
              {/* NEW GAME Button */}
              <button
                onClick={handleStartGame}
                disabled={buttonsDisabled}
                className={`relative group px-12 py-4 transition-all duration-500 w-[320px]
                  ${showButtons ? 'translate-x-0 opacity-100' : 'translate-x-[-100vw] opacity-0'}
                  ${buttonsDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                style={{ transitionDelay: '0ms' }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#4a9eff20] to-[#7b5ee720] rounded-lg" />
                <div className="absolute inset-0 bg-[#0c1220] rounded-lg border border-[#4a9eff40] group-hover:border-[#4a9eff] transition-colors" />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute inset-0 bg-[#4a9eff10] rounded-lg" />
                </div>
                <span className="relative font-['Orbitron'] font-bold text-xl text-[#4a9eff] tracking-wider">
                  NEW GAME
                </span>
              </button>

              {/* LOAD Button */}
              <button
                disabled={buttonsDisabled}
                className={`relative group px-12 py-4 transition-all duration-500 w-[320px] opacity-50
                  ${showButtons ? 'translate-x-0' : 'translate-x-[-100vw] opacity-0'}
                  ${buttonsDisabled ? 'opacity-25 cursor-not-allowed' : ''}`}
                style={{ transitionDelay: '100ms' }}
              >
                <div className="absolute inset-0 bg-[#4a9eff10] rounded-lg" />
                <div className="absolute inset-0 bg-[#0c1220] rounded-lg border border-[#4a9eff20] group-hover:border-[#4a9eff40] transition-colors" />
                <span className="relative font-['Orbitron'] font-bold text-xl text-[#4a9eff80] tracking-wider">
                  LOAD
                </span>
              </button>

              {/* SETTINGS Button */}
              <button
                onClick={() => setShowSettings(true)}
                disabled={buttonsDisabled}
                className={`relative group px-12 py-4 transition-all duration-500 w-[320px]
                  ${showButtons ? 'translate-x-0 opacity-100' : 'translate-x-[-100vw] opacity-0'}
                  ${buttonsDisabled ? 'opacity-25 cursor-not-allowed' : ''}`}
                style={{ transitionDelay: '200ms' }}
              >
                <div className="absolute inset-0 bg-[#4a9eff10] rounded-lg" />
                <div className="absolute inset-0 bg-[#0c1220] rounded-lg border border-[#4a9eff20] group-hover:border-[#4a9eff40] transition-colors" />
                <span className="relative font-['Orbitron'] font-bold text-xl text-[#4a9eff80] tracking-wider">
                  SETTINGS
                </span>
              </button>

              {/* QUIT Button */}
              <button
                disabled={buttonsDisabled}
                className={`relative group px-12 py-4 transition-all duration-500 w-[320px]
                  ${showButtons ? 'translate-x-0 opacity-100' : 'translate-x-[-100vw] opacity-0'}
                  ${buttonsDisabled ? 'opacity-25 cursor-not-allowed' : ''}`}
                style={{ transitionDelay: '300ms' }}
              >
                <div className="absolute inset-0 bg-[#4a9eff10] rounded-lg" />
                <div className="absolute inset-0 bg-[#0c1220] rounded-lg border border-[#4a9eff20] group-hover:border-[#4a9eff40] transition-colors" />
                <span className="relative font-['Orbitron'] font-bold text-xl text-[#4a9eff80] tracking-wider">
                  QUIT
                </span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Settings Modal */}
      {showSettings && <Settings onClose={() => setShowSettings(false)} />}

      {/* Transition Overlay */}
      {isTransitioning && (
        <div
          className="absolute inset-0 z-50 bg-[#4a9eff] transition-all duration-1000 ease-in-out"
          style={{
            opacity: isTransitioning ? 1 : 0,
            filter: 'brightness(1.5) blur(20px)',
          }}
        />
      )}

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(20px); }
        }
        @keyframes scan {
          from { background-position: 0 0; }
          to { background-position: 0 -100%; }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 7s ease-in-out infinite;
        }
        .animate-scan {
          animation: scan 10s linear infinite;
        }
      `}</style>
    </div>
  );
}
